import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../Common/Loading';

interface QnDetails {
    qn_no: string; type_name: string; qn_type: string; complexity: string;
    used_in_t: string; used_in_e: string; prc_nos: string;
    no_prc: number; no_clue: number; no_sol: number;
}
interface QuestionViewData {
    lesson_name: string; total_qns: number; qn_no: string;
    qn_details: QnDetails | null; question_data_string: string;
    content: { question: string; clue: string; principles: string[]; solution: string };
    blob_lesson_path: string; blob_assets_path: string;
    wrong_audio_files: string; correct_audio_files: string;
    country: string; mdcode: string; eccode: string;
}

interface FTypeMeta {
    correctComment: string;
    wrongComment: string;
    dragDrop: boolean;
    dummyWords: string[];
}

interface LTypeOption {
    label: string;
    text: string;
}


// Options are carried with their stable KSD_TR_N row id, instead of relying on
// positional index (which broke when Init_O reshuffled the DOM on each render).
interface OptionEntry {
    text: string;
    rowId: number;
}




const GifBtn: React.FC<{
    name: string; onClick?: () => void; visible?: boolean;
    style?: React.CSSProperties; marginLeft?: string;
}> = ({ name, onClick, visible = true, style, marginLeft }) => {
    const [sfx, setSfx] = useState('n');
    if (!visible) return null;
    return (
        <img
            src={`/Image/${name}_${sfx}.gif`} alt={name} draggable={false}
            style={{ cursor: 'pointer', verticalAlign: 'middle', marginLeft: marginLeft || '0', ...style }}
            onMouseEnter={() => setSfx('o')} onMouseLeave={() => setSfx('n')}
            onMouseDown={() => setSfx('d')} onMouseUp={() => setSfx('o')}
            onClick={onClick}
        />
    );
};

const QuestionViewer: React.FC = () => {
    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
    const [searchParams] = useSearchParams();
    const [data, setData] = useState<QuestionViewData | null>(null);
    const [loading, setLoading] = useState(true);
    const [wTypeAnswer, setWTypeAnswer] = useState<string>('');
    const [wTypeWrongAnswer, setWTypeWrongAnswer] = useState<string>('');
    const [fTypeMeta, setFTypeMeta] = useState<FTypeMeta | null>(null);
    const [extractedOptionsHtml, setExtractedOptionsHtml] = useState<string>('');
    const [lTypeOptions, setLTypeOptions] = useState<LTypeOption[]>([]);

    const iframeQnRef = useRef<HTMLIFrameElement>(null);
    const iframeCluRef = useRef<HTMLIFrameElement>(null);
    const iframeSolRef = useRef<HTMLIFrameElement>(null);
    const prcIframeRefs = useRef<(HTMLIFrameElement | null)[]>([]);
    const audioRef = useRef<any>(null);
    const [qnPanelBg, setQnPanelBg] = useState('#7a96df');

    const country = searchParams.get('Country') || 'Australia';
    const lessonId = searchParams.get('Lessonid') || searchParams.get('Title') || '';
    const qnNoStr = searchParams.get('QnNo') || searchParams.get('Qnno') || '1';
    const mdcode = searchParams.get('mdcode') || '';
    const eccode = searchParams.get('eccode') || '';
    const fromPage = searchParams.get('from') || '';
    const searchWords = searchParams.get('SearchWords') || '';
    const matchType = searchParams.get('match') || '';
    const entCorAns = searchParams.get('EntCorAns') || searchParams.get('EntCrtAns') || '';
    const entWrongAns = searchParams.get('EntWrongAns') || '';
    const entBlankAns = searchParams.get('EntBlankAns') || '';
    const optOrder = searchParams.get('OptOrder') || '';
    const dicCode = searchParams.get('Diccode') || searchParams.get('DICCode') || '';
    const familyCode = searchParams.get('FamilyCode') || '';
    const firstName = searchParams.get('Login') || '';
    const testCode = searchParams.get('TestCode') || '';
    const selDiccode = searchParams.get('selectedDiccode') || '';
    const effectiveDicCode = dicCode || selDiccode;
    const solSeen = searchParams.get('SolSeen') || '';
    const ylaParam = searchParams.get('yla') || '0';


    // Options store { text, rowId } instead of just string
    const [options, setOptions] = useState<OptionEntry[]>([]);
    const [correctOptionIndexFallback, setCorrectOptionIndexFallback] = useState<number | null>(null);

    const isFromInsights = fromPage === 'QuestionInsights';
    const isFromStudentPerf = fromPage === 'EL_Studentperformance';
    const isFromSearchQn = fromPage === 'SearchQnTypes';

    const [insightUrls, setInsightUrls] = useState<string[]>([]);
    const [insightIndex, setInsightIndex] = useState(0);
    const qnNo = parseInt(qnNoStr, 10) || 1;

    const lessonQuestions = JSON.parse(
        sessionStorage.getItem("lessonQuestions") || "[]"
    );

    const currentIndex = lessonQuestions.findIndex(
        (q: any) => Number(q.qn_no) === qnNo
    );
    const assetsPath_ref = useRef('');

    // Guards against onQuestionLoad running twice on the same iframe document
    // (the iframe's native onLoad + the "data ready" effect could both fire it,
    // and Init_O reshuffles the DOM randomly each time it runs).
    const processedDocRef = useRef<Document | null>(null);

    const play = useCallback((path: string) => {
        if (!data) return;
        if (audioRef.current) {
            try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch (_) { }
        }
        const fullPath = data.blob_lesson_path + path;
        audioRef.current = new Audio(fullPath);
        audioRef.current.play().catch((e: any) => console.log('Audio failed:', e));
    }, [data]);

    const injectPlaySoundBridge = useCallback((iframe: HTMLIFrameElement) => {
        try {
            if (iframe.contentWindow) {
                (iframe.contentWindow as any).playSound = (_id: string, path: string) => play(path);
            }
        } catch (_) { }
    }, [play]);

    const adjustIframeHeight = (iframe: HTMLIFrameElement) => {
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc || !doc.body) return;
            iframe.style.height = '0px';
            const tableH = (doc.getElementById('tableqn') as HTMLElement)?.offsetHeight || 0;
            const ksdH = (doc.getElementById('ksd_table') as HTMLElement)?.offsetHeight || 0;
            const bodyH = doc.body.scrollHeight || 0;
            const total = Math.max(tableH + ksdH, bodyH) + 5;
            iframe.style.height = total + 'px';
            iframe.style.overflow = 'hidden';
        } catch (_) { }
    };

    const setIframeHeightToBody = (iframe: HTMLIFrameElement) => {
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc || !doc.body) return;
            iframe.style.height = '0px';
            const h = doc.body.scrollHeight;
            iframe.style.height = (h + 20) + 'px';
            iframe.style.overflow = 'hidden';
            (iframe as any).scrolling = 'no';
        } catch (_) { }
    };

    const adjustIframesolHeight = (iframe: HTMLIFrameElement) => {
        try {
            const doc = iframe.contentDocument || iframe.contentWindow?.document;
            if (!doc || !doc.body) return;
            iframe.style.height = '0px';
            const h = Math.max(doc.body.scrollHeight, doc.body.offsetHeight);
            iframe.style.height = (h + 20) + 'px';
            iframe.style.overflow = 'hidden';
            (iframe as any).scrolling = 'no';
        } catch (_) { }
    };

    const highlightInAllIframes = useCallback(() => {
        if (!searchWords) return;
        const text = decodeURIComponent(searchWords).replace(/\s+/g, ' ').trim();
        if (!text) return;
        const escaped = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, '\\s+');
        const regex = matchType === 'Exactmatch'
            ? new RegExp(`\\b${escaped}\\b`, 'gi')
            : new RegExp(escaped, 'gi');

        const highlightDoc = (doc: Document) => {
            if (!doc.body) return;
            const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
                acceptNode(node) {
                    if (!node.nodeValue?.trim()) return NodeFilter.FILTER_REJECT;
                    const tag = node.parentNode?.nodeName;
                    if (tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
                    return NodeFilter.FILTER_ACCEPT;
                }
            });
            const nodes: Node[] = [];
            let n: Node | null;
            while ((n = walker.nextNode())) { regex.lastIndex = 0; if (regex.test(n.nodeValue || '')) nodes.push(n); }
            nodes.forEach(node => {
                const span = doc.createElement('span');
                span.innerHTML = (node.nodeValue || '').replace(regex,
                    m => `<span style="background-color:yellow;color:black;padding:2px;border-radius:3px;">${m}</span>`);
                node.parentNode?.replaceChild(span, node);
            });
        };

        const allIframes = [iframeQnRef.current, iframeCluRef.current, iframeSolRef.current,
        ...prcIframeRefs.current].filter(Boolean) as HTMLIFrameElement[];

        allIframes.forEach(iframe => {
            const run = () => {
                try {
                    const doc = iframe.contentDocument;
                    if (doc) { setTimeout(() => highlightDoc(doc), 400); }
                } catch (_) { }
            };
            if (iframe.contentDocument?.readyState === 'complete') run();
            else iframe.addEventListener('load', run);
        });
    }, [searchWords, matchType]);

    const parseFTypeMeta = useCallback((doc: Document): FTypeMeta => {
        const infoVal = (doc.getElementById('KSD_MW_Info') as HTMLInputElement)?.value || '';
        const parts = infoVal.split('|');

        const dummyWords: string[] = [];
        doc.querySelectorAll('[id^="KSD_WLIST_DragDrop_"]').forEach((el) => {
            const text = el.textContent?.trim();
            if (text) dummyWords.push(text);
        });
        const dragDrop = dummyWords.length > 0;

        if (dummyWords.length === 0) {
            doc.querySelectorAll('[id^="KSD_FIGTD_"]').forEach((el) => {
                const span = el.querySelector('span');
                const text = span?.textContent?.trim() || el.textContent?.trim();
                if (text) dummyWords.push(text);
            });
        }

        const lastTwo = parts.slice(-2);
        const doesHaveAnswerList = parseInt(parts[3] || '1', 10);
        const correctIndex = doesHaveAnswerList - 1; // 0 or 1

        let correctComment = '';
        let wrongComment = '';
        if (lastTwo.length >= 2) {
            if (correctIndex === 0) {
                correctComment = lastTwo[0] || '';
                wrongComment = lastTwo[1] || '';
            } else {
                correctComment = lastTwo[1] || '';
                wrongComment = lastTwo[0] || '';
            }
        } else if (lastTwo.length === 1) {
            correctComment = lastTwo[0] || '';
        }

        // Filter out meaningless values (original: ignore if value is '0' or single digit)
        const isValidComment = (s: string) =>
            s && s.trim() !== '' && s.trim() !== '0' && !/^\d$/.test(s.trim());

        return {
            correctComment: isValidComment(correctComment) ? correctComment : '',
            wrongComment: isValidComment(wrongComment) ? wrongComment : '',
            dragDrop,
            dummyWords,
        };
    }, []);

    const Init_O = (doc: Document, assetsPath: string) => {
        console.log("INIT_O CALLED");
        const yourOptionsHeader = Array.from(doc.querySelectorAll("td"))
            .find(td => td.textContent?.trim() === "Your Options");

        if (yourOptionsHeader) {
            const rightPanel = yourOptionsHeader.closest("td[valign='top']");

            if (rightPanel) {
                rightPanel.remove();
            }
        }

        const questionStr = (doc.getElementById('KSD_MW_Info') as HTMLInputElement)?.value || '';
        const qDetails = questionStr.split('|');
        const selectedBg = '#7a96df';
        const correctOptionIndex = qDetails[3]; // string, used for name comparison
        const noOfOptionsToShow = parseInt(qDetails[4]) || 0;
        let optionBgColor = 0;
        if (qDetails.length >= 10) {
            const style = qDetails[9] || '';
            optionBgColor = parseInt(style[0]) || 0;
        }
        const tbody = doc.querySelector('#KSD_TABLE>tbody') as HTMLElement;
        if (!tbody) return { ShuffledOptionsOrder: '', NoOfOptions: 0 };


        // Shuffle rows (same double-shuffle as original)
        const rows = Array.from(tbody.querySelectorAll('tr[id^="KSD_TR_"]')) as HTMLElement[];
        for (let i = rows.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            tbody.appendChild(rows[j]);
            const tmp = rows[i]; rows[i] = rows[j]; rows[j] = tmp;
        }
        ['KSD_TR_24', 'KSD_TR_25'].forEach(id => {
            const el = doc.getElementById(id); if (el) tbody.appendChild(el);
        });

        // Trim to NoOfOptionsToShow
        if (noOfOptionsToShow > 0 && noOfOptionsToShow < tbody.querySelectorAll('tr[id^="KSD_TR_"]').length) {
            while (noOfOptionsToShow !== tbody.querySelectorAll('tr[id^="KSD_TR_"]').length) {
                const cur = Array.from(tbody.querySelectorAll('tr[id^="KSD_TR_"]')) as HTMLElement[];
                const rn = Math.floor(Math.random() * cur.length);
                const row = cur[rn];
                const rad = row.querySelector('input[id^="KSD_Rad_"]') as HTMLInputElement;
                if (rad && rad.name !== correctOptionIndex &&
                    row.id !== 'KSD_TR_24' && row.id !== 'KSD_TR_25') {
                    row.remove();
                }
            }
        }

        tbody.querySelectorAll('input[id^="KSD_Rad_"]').forEach((r: any) => r.style.display = 'none');
        tbody.querySelectorAll('td[id="KSD_TD1"]').forEach((td: any) => td.style.display = 'none');

        let shuffled = '';
        const finalRows = Array.from(tbody.querySelectorAll('tr[id^="KSD_TR_"]')) as HTMLElement[];
        finalRows.forEach(row => {
            const rad = row.querySelector('input[id^="KSD_Rad_"]') as HTMLInputElement;
            if (rad) { if (shuffled) shuffled += '~'; shuffled += rad.name; }
        });

        finalRows.forEach((row, idx) => {
            const indexFont = row.querySelector('td[id="KSD_TD1"] font[id^="Index"]');
            if (indexFont) indexFont.textContent = String.fromCharCode(65 + idx);
        });

        if (optionBgColor !== 1) {
            finalRows.forEach((row, i) => {
                row.style.backgroundColor = i % 2 === 0 ? '#FAF0E6' : '#F8F8F8';
                row.style.cursor = 'pointer';
            });
        } else {
            finalRows.forEach(row => {
                row.style.backgroundColor = 'transparent';
                row.style.cursor = 'pointer';
            });
        }

        finalRows.forEach(row => {
            row.addEventListener('click', () => {
                finalRows.forEach((r, i) => {
                    r.style.backgroundColor =
                        optionBgColor === 1 ? 'transparent' : (i % 2 === 0 ? '#FAF0E6' : '#F8F8F8');
                    r.querySelectorAll('input[id^="KSD_Rad_"]').forEach((rad: any) => rad.checked = false);
                });
                row.style.backgroundColor = selectedBg;
                const rad = row.querySelector('input[id^="KSD_Rad_"]') as HTMLInputElement;
                if (rad) rad.checked = true;
            });
        });

        tbody.querySelectorAll('td[id^="KSD_TD_"]').forEach((td: any) => td.style.paddingRight = '20px');
        doc.body?.removeAttribute('onmousemove');
        return { ShuffledOptionsOrder: shuffled, NoOfOptions: noOfOptionsToShow || finalRows.length };
    };

    // MarkAns_O: matches original exactly.
    // correctIdx comes from KSD_MW_Info[3] (numeric).
    // wrongAns: parseInt(entWrongAns) — guarded for NaN.
    // Uses last td of each row for the result image.
    const MarkAns_O = useCallback((doc: Document, assetsPath: string) => {
        setTimeout(() => {
            const questionStr = (doc.getElementById('KSD_MW_Info') as HTMLInputElement)?.value || '';
            const qDetails = questionStr.split('|');
            const correctIdx = parseInt(qDetails[3], 10);

            // Guard NaN
            const wrongAnsRaw = entWrongAns ? parseInt(entWrongAns, 10) : null;
            const wrongAns = (wrongAnsRaw !== null && !Number.isNaN(wrongAnsRaw)) ? wrongAnsRaw : null;

            const tbody = doc.querySelector('#KSD_TABLE>tbody');
            if (!tbody) return;

            // Apply optOrder reorder first
            if (optOrder) {
                const order = optOrder.split('~').map(Number);
                const rows = Array.from(tbody.querySelectorAll('tr[id^="KSD_TR_"]')) as HTMLElement[];
                tbody.innerHTML = '';
                order.forEach(idx => { if (rows[idx]) tbody.appendChild(rows[idx]); });
                rows.forEach((r, i) => { if (!order.includes(i)) tbody.appendChild(r); });
            }

            const correctImg = `<img src='${assetsPath}Image/Questions/EL_O/AnsCorrect.GIF' style='vertical-align:middle;'/>`;
            const wrongImg = `<img src='${assetsPath}Image/Questions/EL_O/AnsWrong.GIF' style='vertical-align:middle;'/>`;

            console.log({
                optOrder,
                correctIdx,
                wrongAns
            });


            tbody.querySelectorAll('tr[id^="KSD_TR_"]').forEach((row: any) => {
                console.log(
                    row.id,
                    (row.querySelector('input[id^="KSD_Rad_"]') as HTMLInputElement)?.name
                );
                // Row index from the id (KSD_TR_N -> N)
                const rowIdx = parseInt(row.id.replace('KSD_TR_', ''), 10);
                console.log(`Row ${rowIdx}: correctIdx=${correctIdx}, wrongAns=${wrongAns}`);
                const tds = row.querySelectorAll('td');
                const last = tds[tds.length - 1] as HTMLElement;
                if (!last) return;
                last.innerHTML = '';
                // Wrong first, then correct overrides
                if (wrongAns !== null && rowIdx === wrongAns) {
                    last.innerHTML = wrongImg;
                }
                if (rowIdx === correctIdx) {
                    last.innerHTML = correctImg;
                }
            });
        }, 500);
    }, [entWrongAns, optOrder]);

    // ---------------------------------------------------------------------------
    // Init_B / MarkAns_B
    // ---------------------------------------------------------------------------
    const Init_B = (doc: Document) => {
        doc.querySelectorAll('body>table').forEach((t: any) => t.style.backgroundColor = 'White');
        doc.body && (doc.body.style.backgroundColor = 'White');
        doc.querySelectorAll('#KSD_TABLE>tbody>tr[id^="KSD_TR_"] td[id^="KSD_TD1"]')
            .forEach((td: any) => td.style.display = 'none');
        doc.body?.removeAttribute('onmousemove');
        const infoEl = doc.getElementById('KSD_MW_Info') as HTMLInputElement;
        if (infoEl) {
            const correctIdx = parseInt(infoEl.value.split('|')[4], 10);
            if (!Number.isNaN(correctIdx)) setCorrectOptionIndexFallback(correctIdx);
        }
        return { NoOfOptions: 2 };
    };

    const MarkAns_B = useCallback((doc: Document) => {
        setTimeout(() => {
            const infoEl = doc.getElementById('KSD_MW_Info') as HTMLInputElement;
            if (!infoEl) return;
            const correctIdx = infoEl.value.split('|')[4]; // string comparison
            doc.querySelectorAll('tr[id^="KSD_TR_"]').forEach((row: any) => {
                const rowIdx = row.id.replace('KSD_TR_', '');
                if (rowIdx === correctIdx) {
                    row.style.backgroundColor = 'green';
                } else {
                    // Original colours ALL other rows red unconditionally
                    row.style.backgroundColor = 'red';
                }
            });
        }, 400);
    }, []);

    const Init_F = useCallback((doc: Document) => {
        doc.querySelectorAll('body>table').forEach((t: any) => t.style.backgroundColor = 'White');
        doc.body && (doc.body.style.backgroundColor = 'White');

        const meta = parseFTypeMeta(doc);
        setFTypeMeta(meta);

        // Remove word-list panel
        const wordPanel = doc.getElementById('KSD_WORDS2FILLUP');
        if (wordPanel) {
            let el: HTMLElement | null = wordPanel;
            while (el && el.tagName !== 'TD') el = el.parentElement;
            if (el) el.remove();
        }
        const divider = doc.getElementById('KSD_DIVIDERBAR');
        if (divider) divider.remove();

        doc.querySelectorAll("td").forEach((td) => {
            if (td.textContent?.trim() === "Number pad" || td.textContent?.includes("Number pad:")) td.remove();
        });

        setTimeout(() => {
            const bw = (doc.getElementById('KSD_BW') as HTMLInputElement)?.value || '';
            if (bw) {
                const answers = bw.split('|');
                doc.querySelectorAll('input[id^="KSD_BLANK_"]').forEach((inp: any, i: number) => {
                    const ans = answers[i] || '';
                    inp.style.width = (15 + ans.length * 10) + 'px';
                });
            }
        }, 500);

        doc.querySelectorAll('input[id^="KSD_BLANK_"]').forEach((inp: any) => {
            Object.assign(inp.style, {
                backgroundColor: 'transparent', color: '#017ebc',
                fontSize: '18pt', textAlign: 'center',
                cursor: 'pointer', fontFamily: 'Verdana'
            });
            inp.addEventListener('paste', (e: Event) => e.preventDefault());
        });

        const questionStr = (doc.getElementById('KSD_MW_Info') as HTMLInputElement)?.value || '';
        const qDetails = questionStr.split('|');
        const retNO = parseInt(qDetails[5]) || 0;
        return { ShuffledOptionsOrder: '', NoOfOptions: retNO };
    }, [parseFTypeMeta]);

    // MarkAns_F: matches original exactly.
    const MarkAns_F = useCallback((doc: Document, assetsPath: string) => {
        setTimeout(() => {
            const infoVal = (doc.getElementById('KSD_MW_Info') as HTMLInputElement)?.value || '';
            const bwVal = (doc.getElementById('KSD_BW') as HTMLInputElement)?.value || '';
            if (!infoVal || !bwVal) return;
            const correctAnswers = bwVal.split('|');
            const hasQuery = entCorAns || entWrongAns || entBlankAns;

            // No query params — show correct answers in blanks (red bold)
            if (!hasQuery) {
                doc.querySelectorAll('span[id^="span_"]').forEach((span: any, idx: number) => {
                    const inp = span.querySelector('input[id^="KSD_BLANK_"]') as HTMLInputElement;
                    if (inp) {
                        Object.assign(inp.style, {
                            borderStyle: 'none', padding: '0px',
                            borderBottom: 'solid 2px black', color: 'red',
                            fontWeight: 'bold', textAlign: 'center', fontFamily: 'Verdana'
                        });
                        inp.value = correctAnswers[idx] || '';
                    }
                });
                return;
            }

            // All correct, no wrong/blank
            if (entCorAns && !entWrongAns && !entBlankAns) {
                const corrArr = entCorAns.split('~');
                doc.querySelectorAll('span[id^="span_"]').forEach((span: any, idx: number) => {
                    const inp = span.querySelector('input[id^="KSD_BLANK_"]') as HTMLInputElement;
                    if (inp) inp.style.display = 'none';
                    Object.assign(span.style, { border: 'none', borderBottom: 'solid 2px red' });
                    const sp = doc.createElement('span');
                    Object.assign(sp.style, { color: 'green', fontFamily: 'Verdana' });
                    sp.innerHTML = (corrArr[idx] || '').substring(2) +
                        `<img id="imgR" src="${assetsPath}Image/Questions/EL_O/AnsCorrect.GIF" style="vertical-align:middle;">`;
                    span.appendChild(sp);
                });
                return;
            }

            // Mixed: some correct, some wrong, some blank
            if (entWrongAns || entBlankAns) {
                if (entCorAns) {
                    entCorAns.split('~').forEach(ca => {
                        const blankIdx = parseInt(ca.substring(0, 2));
                        const blankSpan = doc.querySelectorAll('span[id^="span_"]')[blankIdx] as HTMLElement;
                        if (!blankSpan) return;
                        const inp = blankSpan.querySelector('input[id^="KSD_BLANK_"]') as HTMLInputElement;
                        if (inp) inp.style.display = 'none';
                        Object.assign(blankSpan.style, { border: 'none', borderBottom: 'solid 2px red' });
                        const sp = doc.createElement('span');
                        Object.assign(sp.style, { color: 'green', fontFamily: 'Verdana' });
                        sp.innerHTML = ca.substring(2) + `<img src="${assetsPath}Image/Questions/EL_O/AnsCorrect.GIF" style="vertical-align:middle;">`;
                        blankSpan.appendChild(sp);
                    });
                }
                if (entWrongAns) {
                    entWrongAns.split('~').forEach(wa => {
                        const blankIdx = parseInt(wa.substring(0, 2));
                        const blankSpan = doc.querySelectorAll('span[id^="span_"]')[blankIdx] as HTMLElement;
                        if (!blankSpan) return;
                        const inp = blankSpan.querySelector('input[id^="KSD_BLANK_"]') as HTMLInputElement;
                        if (inp) inp.style.display = 'none';
                        Object.assign(blankSpan.style, { border: 'none', borderBottom: 'solid 2px red' });
                        const wrong = doc.createElement('span');
                        Object.assign(wrong.style, { color: 'red', textDecoration: 'line-through', fontFamily: 'Verdana' });
                        wrong.textContent = wa.substring(2);
                        const correct = doc.createElement('span');
                        Object.assign(correct.style, { color: 'green', fontFamily: 'Verdana' });
                        correct.textContent = correctAnswers[blankIdx] || '';
                        blankSpan.appendChild(wrong);
                        blankSpan.appendChild(doc.createTextNode('\u00a0'));
                        blankSpan.appendChild(correct);
                    });
                }
                if (entBlankAns) {
                    entBlankAns.split('~').forEach(ba => {
                        const blankIdx = parseInt(ba);
                        const blankSpan = doc.querySelectorAll('span[id^="span_"]')[blankIdx] as HTMLElement;
                        if (!blankSpan) return;
                        const inp = blankSpan.querySelector('input[id^="KSD_BLANK_"]') as HTMLInputElement;
                        if (inp) inp.style.border = 'solid 2px red';
                        const correct = doc.createElement('span');
                        Object.assign(correct.style, { color: 'green', fontFamily: 'Verdana' });
                        correct.textContent = correctAnswers[blankIdx] || '';
                        blankSpan.appendChild(doc.createTextNode('\u00a0'));
                        blankSpan.appendChild(correct);
                    });
                }
                return;
            }

            // Skipped (no corr/wrong/blank but hasQuery was truthy — shouldn't happen, safety fallback)
            doc.querySelectorAll('span[id^="span_"]').forEach((span: any, idx: number) => {
                const inp = span.querySelector('input[id^="KSD_BLANK_"]') as HTMLInputElement;
                if (inp) {
                    inp.style.borderStyle = 'none';
                    inp.style.borderBottom = 'solid 2px black';
                }
                const sp = doc.createElement('span');
                Object.assign(sp.style, { color: 'red', fontWeight: 'bold', fontFamily: 'Verdana' });
                sp.textContent = correctAnswers[idx] || '';
                span.appendChild(doc.createTextNode('\u00a0'));
                span.appendChild(sp);
            });
        }, 500);
    }, [entCorAns, entWrongAns, entBlankAns]);

    // ---------------------------------------------------------------------------
    // Init_C / MarkAns_C
    // ---------------------------------------------------------------------------
    const Init_C = useCallback((doc: Document) => {

        doc.querySelectorAll("td").forEach((td) => {
            if (td.textContent?.trim() === "Select answer(s):" && !td.querySelector('table')) {
                td.remove();
            }
        });

        setTimeout(() => {
            const tbody = doc.querySelector('#KSD_TABLE>tbody');
            if (!tbody) return;
            const questionStr = (doc.getElementById('KSD_MW_Info') as HTMLInputElement)?.value || '';
            const qDetails = questionStr.split('|');
            const bgOdd = qDetails[1] || '#FAF0E6';
            const bgEven = qDetails[2] || '#F8F8F8';
            const selectedBg = '#7a96df';
            const rows = Array.from(tbody.querySelectorAll('tr[id^="KSD_TR_"]')) as HTMLElement[];

            if (optOrder) {
                const order = optOrder.split('~').map(Number);
                const detached = rows.map(r => { tbody.removeChild(r); return r; });
                order.forEach(idx => { if (detached[idx]) tbody.appendChild(detached[idx]); });
            } else {
                for (let i = rows.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [rows[i], rows[j]] = [rows[j], rows[i]];
                }
                rows.forEach(r => tbody.appendChild(r));
            }

            const finalRows = Array.from(tbody.querySelectorAll('tr[id^="KSD_TR_"]')) as HTMLElement[];
            finalRows.forEach((row, idx) => {
                const indFont = row.querySelector('font[id^="Ind_"]');
                if (indFont) indFont.textContent = String.fromCharCode(65 + idx);
                row.style.backgroundColor = idx % 2 === 0 ? bgEven : bgOdd;
                row.style.cursor = 'pointer';
                row.addEventListener('click', () => {
                    finalRows.forEach((r, i) => {
                        r.style.backgroundColor = i % 2 === 0 ? bgEven : bgOdd;
                        r.querySelectorAll('input[id^="Chk_"]').forEach((c: any) => c.checked = false);
                    });
                    row.style.backgroundColor = selectedBg;
                    const chk = row.querySelector('input[id^="Chk_"]') as HTMLInputElement;
                    if (chk) chk.checked = true;
                });
            });
            tbody.querySelectorAll('td[id="KSD_TD1"]').forEach((td: any) => td.style.display = 'none');
            doc.body?.removeAttribute('onmousemove');
        }, 500);
    }, [optOrder]);

    const MarkAns_C = useCallback((doc: Document, assetsPath: string) => {
        setTimeout(() => {
            const tbody = doc.querySelector('#KSD_TABLE>tbody');
            if (!tbody) return;
            const base = assetsPath + 'Image/Questions/EL_C/';
            const correctImg = `<img src='${base}AnsCorrect.GIF' style='vertical-align:middle;'/>`;
            const wrongImg = `<img src='${base}AnsWrong.GIF' style='vertical-align:middle;'/>`;
            const shouldImg = `<img src='${base}AnsShouldbe.GIF' style='vertical-align:middle;'/>`;

            const getOrCreateAnsTd = (row: HTMLElement): HTMLElement => {
                let td = row.querySelector('td.result-td') as HTMLElement;
                if (!td) {
                    td = doc.createElement('td');
                    td.className = 'result-td';
                    const lastTd = row.querySelector('td:last-child');
                    if (lastTd) lastTd.before(td);
                    else row.appendChild(td);
                }
                return td;
            };

            let correctOptionIndex: string[] = [];
            let selectedCorrectOptionIndex: string[] = [];
            let selectedWrongOptionIndex: string[] = [];

            if (entCorAns) {
                const parts = entCorAns.split('#');
                correctOptionIndex = parts[0] ? parts[0].split('~') : [];
                selectedCorrectOptionIndex = parts[1] ? parts[1].split('~') : [];
                if (selectedCorrectOptionIndex.length === 1 && selectedCorrectOptionIndex[0].length > 1) {
                    selectedCorrectOptionIndex = selectedCorrectOptionIndex[0].split('');
                }
            }
            if (entWrongAns) {
                selectedWrongOptionIndex = entWrongAns.split('~');
            }

            tbody.querySelectorAll('tr[id^="KSD_TR_"]').forEach((row: any) => {
                getOrCreateAnsTd(row).innerHTML = '';
            });

            if (
                entCorAns &&
                selectedCorrectOptionIndex.length === correctOptionIndex.length &&
                !entWrongAns
            ) {
                tbody.querySelectorAll('tr[id^="KSD_TR_"]').forEach((row: any) => {
                    const idx = row.id.substring(7); // "KSD_TR_" is 7 chars
                    if (correctOptionIndex.indexOf(idx) !== -1) {
                        getOrCreateAnsTd(row).innerHTML = correctImg;
                    }
                });
                return;
            }

            if (entCorAns || entWrongAns) {
                tbody.querySelectorAll('tr[id^="KSD_TR_"]').forEach((row: any) => {
                    const idx = row.id.substring(7);
                    const ansTd = getOrCreateAnsTd(row);
                    if (selectedCorrectOptionIndex.indexOf(idx) !== -1) {
                        ansTd.innerHTML = correctImg;
                    } else if (selectedWrongOptionIndex.indexOf(idx) !== -1) {
                        ansTd.innerHTML = wrongImg;
                    } else if (
                        correctOptionIndex.indexOf(idx) !== -1 &&
                        selectedCorrectOptionIndex.indexOf(idx) === -1
                    ) {
                        ansTd.innerHTML = shouldImg;
                    }
                });
                return;
            }

            const infoEl = doc.getElementById('KSD_MW_Info') as HTMLInputElement;
            if (infoEl) {
                const qParts = infoEl.value.split('|');
                if (qParts.length > 4 && qParts[4]) {
                    correctOptionIndex = [];
                    for (let i = 0; i < qParts[4].length; i++) {
                        correctOptionIndex.push((qParts[4].charCodeAt(i) - 65).toString());
                    }
                }
            }
            tbody.querySelectorAll('tr[id^="KSD_TR_"]').forEach((row: any) => {
                const idx = row.id.substring(7);
                if (correctOptionIndex.indexOf(idx) !== -1) {
                    getOrCreateAnsTd(row).innerHTML = shouldImg;
                }
            });
        }, 400);
    }, [entCorAns, entWrongAns]);

    // ---------------------------------------------------------------------------
    // Init_M / MarkAns_M
    // ---------------------------------------------------------------------------
    const Init_M = useCallback((doc: Document) => {
        setTimeout(() => {
            const tbody = doc.querySelector('#KSD_TABLE>tbody') as HTMLElement;
            if (!tbody) return;

            if (!optOrder) return;

            const originalLHS: Record<number, HTMLElement> = {};
            const originalRHS: Record<number, HTMLElement> = {};

            tbody.querySelectorAll('tr').forEach((tr) => {
                const lhsEl = tr.querySelector('td[id^="LHS2_"]') as HTMLElement | null;
                const rhsEl = tr.querySelector('td[id^="RHS2_"]') as HTMLElement | null;
                if (lhsEl) {
                    const lhsIdx = parseInt(lhsEl.id.replace('LHS2_', ''), 10);
                    originalLHS[lhsIdx] = lhsEl.cloneNode(true) as HTMLElement;
                }
                if (rhsEl) {
                    const rhsIdx = parseInt(rhsEl.id.replace('RHS2_', ''), 10);
                    originalRHS[rhsIdx] = rhsEl.cloneNode(true) as HTMLElement;
                }
            });

            const lhsOrder = optOrder.split('~')[0].split(',').map(Number);

            let rhsOrder: number[];
            if (entWrongAns) {
                rhsOrder = entWrongAns.indexOf('~') > -1
                    ? entWrongAns.split('~').map(Number)
                    : entWrongAns.split(',').map(Number);
            } else {
                rhsOrder = [...lhsOrder];
            }

            tbody.innerHTML = '';
            for (let i = 0; i < lhsOrder.length; i++) {
                const lhsIdx = lhsOrder[i];
                const rhsIdx = rhsOrder[i];
                if (originalLHS[lhsIdx] === undefined || originalRHS[rhsIdx] === undefined) continue;
                const tr = doc.createElement('tr');
                tr.appendChild(originalLHS[lhsIdx].cloneNode(true));
                tr.appendChild(originalRHS[rhsIdx].cloneNode(true));
                tbody.appendChild(tr);
            }
        }, 500);
    }, [optOrder, entWrongAns]);

    const MarkAns_M = useCallback((doc: Document, assetsPath: string) => {
        setTimeout(() => {
            const tbody = doc.querySelector('#KSD_TABLE>tbody');
            if (!tbody) return;
            const base = assetsPath + 'Image/Questions/EL_M/';

            tbody.querySelectorAll('tr').forEach((row: any) => {
                const lhsEl = row.querySelector('td[id^="LHS"]') as HTMLElement | null;
                const rhsEl = row.querySelector('td[id^="RHS"]') as HTMLElement | null;
                if (!lhsEl || !rhsEl) return;

                const lhsIndex = lhsEl.id.split('_').pop() || '';
                const rhsIndex = rhsEl.id.split('_').pop() || '';

                const existingAns = row.querySelector('td[id^="TDANS_"]');
                if (existingAns) existingAns.remove();

                const ansTd = doc.createElement('td');
                ansTd.id = 'TDANS_';
                if (lhsIndex === rhsIndex) {
                    ansTd.innerHTML = `<img src='${base}AnsCorrect.GIF' alt='Correct' style='vertical-align:middle;'/>`;
                } else {
                    ansTd.innerHTML = `<img src='${base}AnsWrong.GIF' alt='Wrong' style='vertical-align:middle;'/>`;
                }
                row.appendChild(ansTd);
            });
        }, 500);
    }, []);

    // ---------------------------------------------------------------------------
    // Init_L / MarkAns_L
    // ---------------------------------------------------------------------------
    const MarkAns_L = useCallback((doc: Document, assetsPath: string) => {
        setTimeout(() => {
            const base = assetsPath + 'Image/Questions/EL_L/';
            doc.querySelectorAll('#KSD_TABLE>tbody>tr[id^="KSD_TR_"]>td[id^="KSD_TD1"]')
                .forEach((td: any) => td.style.width = '0%');

            const setResult = (outerTr: HTMLElement, imgName: string) => {
                const innerTr = outerTr.querySelector('table table tr') as HTMLElement;
                if (!innerTr) return;
                let resTd = innerTr.querySelector('td.result_td') as HTMLElement;
                if (!resTd) {
                    resTd = doc.createElement('td');
                    resTd.className = 'result_td';
                    innerTr.appendChild(resTd);
                }
                resTd.innerHTML = `<img align="absmiddle" src="${base}${imgName}"/>`;
            };

            const rows = doc.querySelectorAll('#KSD_TABLE>tbody>tr[id^="TR_"]');

            if (entCorAns && !entWrongAns) {
                rows.forEach(r => setResult(r as HTMLElement, 'AnsCorrect.GIF'));
            } else if (entWrongAns) {
                const corrArr = entCorAns ? entCorAns.split('~') : [];
                const wrongArr = entWrongAns.split('~');
                rows.forEach((row: any) => {
                    const rowIdx = row.id.substring(3); // "TR_" is 3 chars
                    if (corrArr.includes(rowIdx)) setResult(row, 'AnsCorrect.GIF');
                    else if (wrongArr.includes(rowIdx)) setResult(row, 'AnsWrong.GIF');
                });
            } else {
                rows.forEach(r => setResult(r as HTMLElement, 'AnsShouldbe.GIF'));
            }
        }, 500);
    }, [entCorAns, entWrongAns]);

    // Note: for L-type, the visible "Options:" table below the iframe (built
    // from lTypeOptions in onQuestionLoad) is the source of truth for what
    // the student sees, ordered per OptOrder with the original letter kept.
    // Init_L here only needs to clean up / hide the original iframe table so
    // it isn't shown twice — it does NOT need to reorder DOM rows itself.
    const Init_L = useCallback((doc: Document) => {
        const divider = doc.getElementById("KSD_DIVIDERBAR");
        divider?.remove();

        // Hide the original options table inside the iframe.
        // React renders the reordered options separately, below the iframe.
        const table = doc.getElementById("KSD_TABLE") as HTMLElement | null;
        if (table) {
            table.style.display = "none";
        }
    }, []);

    const Init_N = (_doc: Document) => ({ NoOfOptions: '' });

    const MarkAns_N = useCallback((doc: Document, assetsPath: string) => {
        setTimeout(() => {
            const correctAnswers = ((doc.getElementById('KSD_BW') as HTMLInputElement)?.value || '').split('|');
            const hasQuery = entCorAns || entWrongAns || entBlankAns;

            if (!hasQuery) {
                doc.querySelectorAll('input[id^="KSD_BLANK_"]').forEach((inp: any, idx: number) => {
                    inp.style.display = 'none';
                    const sp = doc.createElement('span');
                    sp.style.color = 'RED';
                    sp.textContent = correctAnswers[idx] || '';
                    inp.insertAdjacentElement('afterend', sp);
                });
                doc.querySelectorAll('input[id^="TXT_CARRY_"]').forEach((inp: any) => {
                    if (inp.getAttribute('name') !== '-') {
                        inp.style.display = 'none';
                        const sp = doc.createElement('span');
                        Object.assign(sp.style, { fontSize: '15pt', marginLeft: '20px' });
                        sp.textContent = inp.getAttribute('name') || '';
                        inp.insertAdjacentElement('afterend', sp);
                    }
                });
                return;
            }

            if (entCorAns && !entWrongAns && !entBlankAns) {
                const blankPart = entCorAns.split(',')[0];
                blankPart.split('~').forEach(ca => {
                    const idx = parseInt(ca.substring(0, 2));
                    const inp = doc.querySelectorAll('input[id^="KSD_BLANK_"]')[idx] as HTMLInputElement;
                    if (!inp) return;
                    const sp = doc.createElement('span');
                    Object.assign(sp.style, { color: 'green', borderBottom: '2px solid red' });
                    sp.textContent = ca.substring(2);
                    inp.insertAdjacentElement('afterend', sp);
                    inp.style.display = 'none';
                });
                if (entCorAns.indexOf(',') !== -1) {
                    const carryPart = entCorAns.split(',')[1];
                    carryPart.split('~').forEach(ca => {
                        const idx = parseInt(ca.substring(0, 2));
                        const inp = doc.querySelectorAll('input[id^="TXT_CARRY_"]')[idx] as HTMLInputElement;
                        if (!inp) return;
                        const sp = doc.createElement('span');
                        Object.assign(sp.style, { fontSize: '15pt', marginLeft: '20px' });
                        sp.textContent = ca.substring(2);
                        inp.insertAdjacentElement('afterend', sp);
                        inp.style.display = 'none';
                    });
                }
                return;
            }

            if (entWrongAns || entBlankAns) {
                if (entCorAns) {
                    entCorAns.split(',')[0].split('~').forEach(ca => {
                        const idx = parseInt(ca.substring(0, 2));
                        const inp = doc.querySelectorAll('input[id^="KSD_BLANK_"]')[idx] as HTMLInputElement;
                        if (!inp) return;
                        const sp = doc.createElement('span');
                        Object.assign(sp.style, { color: 'green', borderBottom: '2px solid red' });
                        sp.textContent = ca.substring(2);
                        inp.insertAdjacentElement('afterend', sp);
                        inp.style.display = 'none';
                    });
                    if (entCorAns.indexOf(',') !== -1) {
                        entCorAns.split(',')[1].split('~').forEach(ca => {
                            const idx = parseInt(ca.substring(0, 2));
                            const inp = doc.querySelectorAll('input[id^="TXT_CARRY_"]')[idx] as HTMLInputElement;
                            if (!inp) return;
                            const sp = doc.createElement('span');
                            Object.assign(sp.style, { fontSize: '15pt', marginLeft: '20px' });
                            sp.textContent = ca.substring(2);
                            inp.insertAdjacentElement('afterend', sp);
                            inp.style.display = 'none';
                        });
                    }
                }
                if (entWrongAns) {
                    entWrongAns.split(',')[0].split('~').forEach(wa => {
                        const idx = parseInt(wa.substring(0, 2));
                        const inp = doc.querySelectorAll('input[id^="KSD_BLANK_"]')[idx] as HTMLInputElement;
                        if (!inp) return;
                        const wrong = doc.createElement('span');
                        Object.assign(wrong.style, {
                            color: 'red', backgroundColor: 'yellow', textDecoration: 'line-through'
                        });
                        wrong.textContent = wa.substring(2);
                        const correct = doc.createElement('span');
                        Object.assign(correct.style, { color: 'green' });
                        correct.textContent = correctAnswers[idx] || '';
                        const parent = doc.createElement('span');
                        Object.assign(parent.style, { borderBottom: '2px solid red' });
                        parent.appendChild(wrong);
                        parent.appendChild(doc.createTextNode('\u00a0'));
                        parent.appendChild(correct);
                        inp.insertAdjacentElement('afterend', parent);
                        inp.style.display = 'none';
                    });
                    if (entWrongAns.indexOf(',') !== -1) {
                        entWrongAns.split(',')[1].split('~').forEach(wa => {
                            const idx = parseInt(wa.substring(0, 2));
                            const inp = doc.querySelectorAll('input[id^="TXT_CARRY_"]')[idx] as HTMLInputElement;
                            if (!inp) return;
                            const parent = doc.createElement('span');
                            Object.assign(parent.style, { fontSize: '15pt', marginLeft: '20px' });
                            const ws = doc.createElement('span');
                            ws.textContent = wa.substring(2);
                            parent.appendChild(ws);
                            inp.insertAdjacentElement('afterend', parent);
                            inp.style.display = 'none';
                        });
                    }
                }
                if (entBlankAns) {
                    entBlankAns.split(',')[0].split('~').forEach(ba => {
                        const idx = parseInt(ba);
                        const inp = doc.querySelectorAll('input[id^="KSD_BLANK_"]')[idx] as HTMLInputElement;
                        if (!inp) return;
                        inp.style.backgroundColor = 'yellow';
                        inp.style.border = '1px dashed black';
                        const sp = doc.createElement('span');
                        sp.style.color = 'green';
                        sp.textContent = correctAnswers[idx] || '';
                        inp.insertAdjacentElement('afterend', sp);
                    });
                    if (entBlankAns.indexOf(',') !== -1) {
                        entBlankAns.split(',')[1].split('~').forEach(ba => {
                            const idx = parseInt(ba);
                            const inp = doc.querySelectorAll('input[id^="TXT_CARRY_"]')[idx] as HTMLInputElement;
                            if (!inp) return;
                            inp.style.border = '1px dashed black';
                        });
                    }
                }
                return;
            }

            doc.querySelectorAll('input[id^="KSD_BLANK_"], input[id^="TXT_CARRY_"]').forEach((inp: any) => {
                inp.style.border = '1px dashed black';
            });
            doc.querySelectorAll('input[id^="KSD_BLANK_"]').forEach((inp: any, idx: number) => {
                const sp = doc.createElement('span');
                sp.style.color = 'green';
                sp.textContent = correctAnswers[idx] || '';
                inp.insertAdjacentElement('afterend', sp);
            });
        }, 400);
    }, [entCorAns, entWrongAns, entBlankAns]);

    // ---------------------------------------------------------------------------
    // Init_W / MarkAns_W
    // ---------------------------------------------------------------------------
    const Init_W = (_doc: Document) => ({ NoOfOptions: '' });

    const MarkAns_W = useCallback((doc: Document) => {
        setTimeout(() => {

            const answerTable = doc.getElementById('KSD_TABLE');
            if (answerTable) answerTable.remove();

            const keypad = doc.getElementById('KSD_W_PAD');
            if (keypad?.parentElement) keypad.parentElement.remove();

            doc.querySelectorAll('td').forEach((td) => {
                if (td.textContent?.includes('Number pad')) td.remove();
            });

        }, 500);
    }, []);

    // ---------------------------------------------------------------------------
    // Init_D / MarkAns_D
    // ---------------------------------------------------------------------------
    const Init_D = (doc: Document) => {
        setTimeout(() => {
            doc.querySelectorAll('table select[id="KSD_SELECT"]').forEach((sel: any) => {
                const origOpts = Array.from(sel.options) as HTMLOptionElement[];
                const idxs = origOpts.map((_, i) => i);
                while (sel.options.length) sel.remove(0);
                while (idxs.length) {
                    const rnd = Math.floor(Math.random() * idxs.length);
                    sel.appendChild(origOpts[idxs[rnd]].cloneNode(true));
                    idxs.splice(rnd, 1);
                }
            });
        }, 500);
    };

    const MarkAns_D = useCallback((doc: Document, assetsPath: string) => {
        setTimeout(() => {
            const corrArr = entCorAns ? entCorAns.split('~') : [];
            const wrongArr = entWrongAns ? entWrongAns.split('~') : [];
            const sels = doc.querySelectorAll('table select[id="KSD_SELECT"]');
            const correctImgHtml = `<img src="${assetsPath}Image/Questions/EL_O/AnsCorrect.GIF" style="vertical-align:middle;"/>`;

            if (entCorAns && !entWrongAns) {
                corrArr.forEach(ca => {
                    const sel = sels[parseInt(ca.substring(0, 2), 10)] as HTMLSelectElement;
                    if (!sel) return;
                    const sp = doc.createElement('span');
                    Object.assign(sp.style, { color: 'green', borderBottom: '2px solid red', margin: '3px' });
                    sp.textContent = ca.substring(2);
                    sp.innerHTML += correctImgHtml;
                    sel.insertAdjacentElement('afterend', sp);
                    sel.style.display = 'none';
                });
                return;
            }

            if (entWrongAns) {
                corrArr.forEach(ca => {
                    const sel = sels[parseInt(ca.substring(0, 2), 10)] as HTMLSelectElement;
                    if (!sel) return;
                    const sp = doc.createElement('span');
                    Object.assign(sp.style, { color: 'green', borderBottom: '2px solid red', margin: '3px' });
                    sp.textContent = ca.substring(2);
                    sp.innerHTML += correctImgHtml;
                    sel.insertAdjacentElement('afterend', sp);
                    sel.style.display = 'none';
                });
                wrongArr.forEach(wa => {
                    const sel = sels[parseInt(wa.substring(0, 2), 10)] as HTMLSelectElement;
                    if (!sel) return;
                    const parent = doc.createElement('span');
                    Object.assign(parent.style, { borderBottom: '2px solid red', margin: '3px' });
                    const ws = doc.createElement('span');
                    Object.assign(ws.style, { color: 'red', textDecoration: 'line-through' });
                    ws.textContent = wa.substring(2);
                    const cs = doc.createElement('span');
                    cs.style.color = 'green';
                    cs.textContent = (sel.querySelector('option[value="1"]') as HTMLOptionElement)?.text || '';
                    parent.appendChild(ws);
                    parent.appendChild(doc.createTextNode('\u00a0'));
                    parent.appendChild(cs);
                    sel.insertAdjacentElement('afterend', parent);
                    sel.style.display = 'none';
                });
                return;
            }

            sels.forEach((sel: any) => {
                const sp = doc.createElement('span');
                Object.assign(sp.style, { color: 'green', borderBottom: '2px solid red', margin: '3px' });
                sp.textContent = (sel.querySelector('option[value="1"]') as HTMLOptionElement)?.text || '';
                sel.insertAdjacentElement('afterend', sp);
            });
        }, 500);
    }, [entCorAns, entWrongAns]);

    // ---------------------------------------------------------------------------
    // Init_I / MarkAns_I
    // ---------------------------------------------------------------------------
    const Init_I = (doc: Document) => {
        setTimeout(() => {
            const infoEl = doc.getElementById('KSD_MW_Info') as HTMLInputElement;
            if (!infoEl) return;
            const qDetails = infoEl.value.split('|');
            const noOpts = parseInt(qDetails[0], 10) || 0;
            const textColor = qDetails[noOpts + 1] || '#000000';
            const hoverBorder = qDetails[noOpts + 2] || '#999999';
            const selBorder = qDetails[noOpts + 3] || '#006600';
            const fontSize = qDetails[noOpts + 4] || '12';
            const fontFamily = qDetails[noOpts + 5] || 'Arial';

            const wordOptions: string[] = [];
            for (let i = 0; i < noOpts; i++) wordOptions.push(qDetails[i + 1] || '0N');

            let html = `<center><font style="font-family:${fontFamily};font-size:${fontSize}pt;color:${textColor};">`;
            let j = 1;
            wordOptions.forEach(opt => {
                if (opt === '0N<NEW LINE>') { html += '<br>'; }
                else if (opt[0] === '1' || opt[0] === '2') {
                    html += `<span id="spanWordOption${j}" class="word-option">&nbsp;${opt.substring(2)}&nbsp;</span>`;
                    j++;
                }
            });
            html += '</font></center>';

            const tdTypeI = doc.getElementById('KSD_TDTYPEI');
            if (tdTypeI) { tdTypeI.innerHTML = html; tdTypeI.style.border = 'none'; }

            doc.querySelectorAll('.word-option').forEach((sp: any) => {
                sp.style.cursor = 'pointer';
                sp.addEventListener('click', () => {
                    if (sp.title === 'selected') { sp.style.border = 'none'; sp.title = ''; }
                    else { sp.style.border = `solid 2px ${selBorder}`; sp.title = 'selected'; }
                });
                sp.addEventListener('mouseover', () => {
                    if (sp.title !== 'selected') sp.style.border = `solid 2px ${hoverBorder}`;
                });
                sp.addEventListener('mouseout', () => {
                    if (sp.title !== 'selected') sp.style.border = 'none';
                });
            });
        }, 400);
        return { NoOfOptions: '' };
    };

    const MarkAns_I = useCallback((doc: Document, assetsPath: string) => {
        setTimeout(() => {
            const infoEl = doc.getElementById('KSD_MW_Info') as HTMLInputElement;
            if (!infoEl) return;
            const qDetails = infoEl.value.split('|');
            const noOpts = parseInt(qDetails[0], 10) || 0;
            const wordOptions: string[] = [];
            for (let i = 0; i < noOpts; i++) {
                if (qDetails[i + 1] !== '0N<NEW LINE>') wordOptions.push(qDetails[i + 1] || '0N');
            }

            const base = assetsPath + 'Image/Questions/EL_I/';
            const corrArr = entCorAns ? entCorAns.split('~').map(Number) : [];
            const wrongArr = entWrongAns ? entWrongAns.split('~').map(Number) : [];
            const blankArr = entBlankAns ? entBlankAns.split('~').map(Number) : [];

            const hasAnyAnswer = entCorAns || entWrongAns || entBlankAns;

            if (hasAnyAnswer) {
                doc.querySelectorAll('.word-option').forEach((sp: any) => {
                    const optIdx = parseInt(sp.id.substring(14)); // "spanWordOption" = 14 chars
                    if (corrArr.includes(optIdx)) {
                        sp.innerHTML += `<img align="absmiddle" width="17px" src="${base}AnsCorrect.png"/>`;
                        sp.style.border = 'solid 2px #77c84b';
                        sp.style.margin = '0 4px';
                    } else if (wrongArr.includes(optIdx)) {
                        sp.innerHTML += `<img align="absmiddle" width="17px" src="${base}AnsWrong.png"/>`;
                        sp.style.border = 'solid 2px #e21818';
                        sp.style.margin = '0 4px';
                    } else if (blankArr.includes(optIdx)) {
                        sp.style.border = 'solid 2px #757575';
                        sp.style.margin = '0 4px';
                    }
                });
            } else {
                doc.querySelectorAll('.word-option').forEach((sp: any, index: number) => {
                    if (wordOptions[index] && wordOptions[index][1] === 'Y') {
                        sp.style.backgroundColor = '#006600';
                        sp.style.color = 'white';
                    }
                });
            }
        }, 500);
    }, [entCorAns, entWrongAns, entBlankAns]);

    // ---------------------------------------------------------------------------
    // runInitAndMark
    // ---------------------------------------------------------------------------
    const runInitAndMark = useCallback((doc: Document, qnType: string, assetsPath: string) => {
        assetsPath_ref.current = assetsPath;
        switch (qnType.toUpperCase()) {
            case 'O': Init_O(doc, assetsPath); MarkAns_O(doc, assetsPath); break;
            case 'B': Init_B(doc); MarkAns_B(doc); break;
            case 'F': Init_F(doc); MarkAns_F(doc, assetsPath); break;
            case 'C': Init_C(doc); MarkAns_C(doc, assetsPath); break;
            case 'M': Init_M(doc); MarkAns_M(doc, assetsPath); break;
            case 'L': Init_L(doc); break;
            case 'N': Init_N(doc); MarkAns_N(doc, assetsPath); break;
            case 'W': Init_W(doc); MarkAns_W(doc); break;
            case 'D': Init_D(doc); MarkAns_D(doc, assetsPath); break;
            case 'I': Init_I(doc); MarkAns_I(doc, assetsPath); break;
            default: break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        MarkAns_O, MarkAns_B, Init_F, MarkAns_F,
        Init_C, MarkAns_C, Init_M, MarkAns_M,
        Init_L, MarkAns_N, MarkAns_W, MarkAns_D, MarkAns_I
    ]);

    const extractOptionsTable = useCallback((doc: Document) => {
        try {
            const table = doc.querySelector('#KSD_TABLE') as HTMLElement | null;
            if (!table) return;

            // For L type, don't clone the HTML table.
            // React renders the reordered options from lTypeOptions instead.
            if (data?.qn_details?.qn_type?.toUpperCase() === "L") {
                table.style.display = "none";
                return;
            }

            const clone = table.cloneNode(true) as HTMLElement;
            clone.removeAttribute('id');
            // Some question templates (e.g. sidebar-style B-type layouts) hardcode a
            // narrow fixed width (e.g. width='170px') meant for a 250px sidebar column
            // in the original iframe layout. Once extracted into the full-width
            // "Options:" block below the iframe, that fixed width makes it render as
            // a skinny strip. Strip it so it stretches to match other option tables.
            clone.removeAttribute('width');
            clone.style.width = '100%';

            setExtractedOptionsHtml(clone.outerHTML);
            table.style.display = 'none';
        } catch (_) { }
    }, [data]);

    // ---------------------------------------------------------------------------
    // iframe load handlers
    // ---------------------------------------------------------------------------
    const onClueLoad = useCallback(() => {
        const iframe = iframeCluRef.current;
        if (!iframe) return;
        injectPlaySoundBridge(iframe);
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;
        Object.assign(iframe.style, { width: '98.7%', border: '1.4px solid black', marginLeft: '10px' });
        doc.body && (doc.body.style.backgroundColor = '#F5DEB3');
        doc.querySelectorAll('div[id^="divShow"]').forEach((div: any) => { div.style.display = 'block'; });
        setTimeout(() => setIframeHeightToBody(iframe), 200);
        setTimeout(() => setIframeHeightToBody(iframe), 600);
    }, [injectPlaySoundBridge]);

    const onPrincipleLoad = useCallback((iframe: HTMLIFrameElement) => {
        injectPlaySoundBridge(iframe);
        setTimeout(() => setIframeHeightToBody(iframe), 200);
        setTimeout(() => setIframeHeightToBody(iframe), 600);
    }, [injectPlaySoundBridge]);

    const onSolutionLoad = useCallback(() => {
        const iframe = iframeSolRef.current;
        if (!iframe) return;
        injectPlaySoundBridge(iframe);
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc || !doc.body) return;

        doc.querySelectorAll('p').forEach((p: any) => Object.assign(p.style, { margin: '0', padding: '0' }));
        doc.body.removeAttribute('bgcolor');
        Object.assign(doc.body.style, { margin: '0', padding: '0' });

        const rows = Array.from(doc.querySelectorAll('body > table > tbody > tr'));
        if (rows.length > 0) {
            const newTable = doc.createElement('table');
            newTable.setAttribute('border', '1');
            newTable.setAttribute('width', '100%');
            newTable.setAttribute('cellpadding', '6');
            newTable.setAttribute('cellspacing', '0');
            newTable.style.borderCollapse = 'collapse';
            newTable.style.marginLeft = '10px';
            newTable.style.width = '98.7%';
            // newTable.style.backgroundColor = '#f0f8ff';

            rows.forEach((row: any) => {
                const tds = row.querySelectorAll('td');
                const td2 = tds[1]?.cloneNode(true) as HTMLElement;
                if (td2) { td2.querySelectorAll('*').forEach((el: any) => el.removeAttribute('style')); td2.removeAttribute('style'); }
                const ksdDiv = row.querySelector('div[id^="KsdDiv"]')?.cloneNode(true) as HTMLElement;
                if (ksdDiv) { ksdDiv.querySelectorAll('*').forEach((el: any) => el.removeAttribute('style')); ksdDiv.removeAttribute('style'); }
                const newRow = doc.createElement('tr');
                newRow.style.border = '1px solid #9a9a9a';
                const c1 = doc.createElement('td');
                c1.style.verticalAlign = 'top'; c1.style.padding = '6px'; c1.style.width = '50%';
                c1.innerHTML = td2?.innerHTML || '';
                const c2 = doc.createElement('td');
                c2.style.verticalAlign = 'top'; c2.style.padding = '6px'; c2.style.width = '50%';
                if (ksdDiv) c2.appendChild(ksdDiv);
                newRow.appendChild(c1); newRow.appendChild(c2);
                newTable.appendChild(newRow);
            });
            doc.body.innerHTML = '';
            doc.body.appendChild(newTable);
        }
        setTimeout(() => adjustIframesolHeight(iframe), 200);
        setTimeout(() => adjustIframesolHeight(iframe), 700);
    }, [injectPlaySoundBridge]);

    // ---------------------------------------------------------------------------
    // onQuestionLoad
    // ---------------------------------------------------------------------------
    const onQuestionLoad = useCallback(() => {
        const iframe = iframeQnRef.current;
        if (!iframe || !data) return;
        injectPlaySoundBridge(iframe);

        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;

        // GUARD: don't re-run Init/Mark on a document we've already processed —
        // Init_O reshuffles the DOM randomly each call, which was breaking the
        // sidebar/marked-table row alignment when this handler fired twice.
        if (processedDocRef.current === doc) return;
        processedDocRef.current = doc;

        const qnType = data.qn_details?.qn_type?.toUpperCase() || '';

        if (qnType === "L") {
            const lExtracted: LTypeOption[] = [];

            doc.querySelectorAll("#KSD_TABLE tr[id^='TR_']").forEach((row) => {
                const td1 = row.querySelector("td[id^='TD1_']");
                const td2 = row.querySelector("td[id^='TD2_']");

                lExtracted.push({
                    label: td1?.textContent?.trim() || "",
                    text: td2?.textContent?.trim() || td2?.innerHTML?.trim() || ""
                });
            });

            // Reorder to match the order the student actually placed the items in
            // (OptOrder holds the original row indices in that answer order).
            // The label stays tied to the original row, so the correct letter is
            // preserved — only the row *position* changes.
            if (optOrder) {
                const order = optOrder
                    .split("~")
                    .map(n => parseInt(n, 10))
                    .filter(n => !Number.isNaN(n));

                setLTypeOptions(
                    order.map(i => lExtracted[i]).filter(Boolean)
                );
            } else {
                setLTypeOptions(lExtracted);
            }

            // Hide the original table inside the iframe — the reordered
            // version is rendered by React below the iframe instead.
            const table = doc.getElementById("KSD_TABLE");
            if (table) {
                (table as HTMLElement).style.display = "none";
            }
        }

        doc.querySelectorAll('td').forEach((td) => {
            if (td.textContent?.trim() === 'Numeric Keypad') td.remove();
        });
        doc.querySelectorAll('input[id^="KSD_Rad_"]').forEach((radio) => {
            (radio as HTMLElement).style.display = 'none';
        });

        if (doc.body) { doc.body.style.margin = '0'; doc.body.style.padding = '0'; }
        doc.querySelectorAll('p').forEach((p: any) => { p.style.margin = '0'; p.style.padding = '0'; });

        // ---------------------------------------------------------------
        // Extract options for the sidebar.
        //
        // IMPORTANT: this MUST run before any DOM removal below (e.g. the
        // divOptions removal for type 'O'). querySelectorAll only finds
        // elements that are still attached to `doc` — if we query after
        // divOptions (which contains #KSD_TABLE) has been removed, we get
        // zero rows and the sidebar renders empty.
        //
        // Each option also carries its stable KSD_TR_N row id (not just its
        // position in the NodeList), so the later ✓/✗ status lookup stays
        // correct even after Init_O randomly reshuffles the table.
        // ---------------------------------------------------------------
        const rows = doc.querySelectorAll('tr[id^="KSD_TR_"]');
        const extracted: OptionEntry[] = [];
        rows.forEach((row, i) => {
            const optionTd =
                (row.querySelector('td[id="KSD_TD2"]') as HTMLElement)
                || (row.querySelector('td[id^="KSD_TD_"]:not([id="KSD_TD1"])') as HTMLElement)
                || (row.querySelector('td:last-child') as HTMLElement);
            if (optionTd) {
                const rowIdMatch = (row.id || '').match(/^KSD_TR_(\d+)$/);
                const rowId = rowIdMatch ? parseInt(rowIdMatch[1], 10) : i;
                extracted.push({
                    text: optionTd.textContent?.trim() || optionTd.innerHTML || '',
                    rowId,
                });
            }
        });

        if (qnType === 'M') {
            const lhsItems = doc.querySelectorAll('[id^="LHS2_"]');
            const extractedM: OptionEntry[] = [];
            lhsItems.forEach((item, i) => {
                const idAttr = item.id || '';
                const rowId = parseInt(idAttr.replace('LHS2_', ''), 10);
                extractedM.push({
                    text: item.innerHTML?.trim() || '',
                    rowId: Number.isNaN(rowId) ? i : rowId,
                });
            });
            setOptions(extractedM);
        }

        if (qnType === 'O') {
            const divOptions = doc.getElementById('divOptions');
            if (divOptions) divOptions.remove();
        }

        if (qnType === 'B') {
            setOptions([
                { text: 'True', rowId: 0 },
                { text: 'False', rowId: 1 },
            ]);
            // Remove KSD_TABLE for B type (original removes it for display in sidebar only)
            // DO NOT remove it here because MarkAns_B needs it — it's already in the iframe
        } else if (qnType !== 'M') {
            if (extracted.length > 0) setOptions(extracted);
        }

        doc.addEventListener('keydown', (e: KeyboardEvent) => {
            if ((e.altKey || e.ctrlKey) && e.keyCode === 37) { e.preventDefault(); return false as any; }
        });
        doc.body?.removeAttribute('onmouseup');
        doc.body?.removeAttribute('onmousemove');

        if (qnType === 'B' || qnType === 'O') {
            const divOptions = doc.getElementById('divOptions');
            if (divOptions) divOptions.remove();
        }

        if (qnType !== 'F') {
            doc.querySelectorAll('td').forEach(td => {
                if (td.textContent?.includes('Select an answer') && !td.querySelector('table')) {
                    td.remove();
                }
            });
        }

        // Set fallback correct index for O type
        if (qnType === 'O') {
            const infoVal = (doc.getElementById('KSD_MW_Info') as HTMLInputElement)?.value || '';
            const parsed = parseInt(infoVal.split('|')[3], 10);
            if (!Number.isNaN(parsed)) setCorrectOptionIndexFallback(parsed);
        }


        // Set fallback correct index for B type
        if (qnType === 'B') {
            const infoVal = (doc.getElementById('KSD_MW_Info') as HTMLInputElement)?.value || '';
            const parsed = parseInt(infoVal.split('|')[4], 10);
            if (!Number.isNaN(parsed)) setCorrectOptionIndexFallback(parsed);
        }

        runInitAndMark(doc, qnType, data.blob_assets_path);
        // Remove white/aliceblue backgrounds from question content
        doc.querySelectorAll<HTMLElement>("table, tr, td, div").forEach((el) => {
            const bg = el.style.backgroundColor.toLowerCase();

            if (
                bg === "#ffffff" ||
                bg === "white" ||
                bg === "rgb(255, 255, 255)" ||
                bg === "#f0f8ff" ||
                bg === "aliceblue" ||
                bg === "rgb(240, 248, 255)"
            ) {
                el.style.backgroundColor = "transparent";
            }
        });

        doc.querySelectorAll<HTMLElement>("[bgcolor]").forEach(el => {
            if (
                el.getAttribute("bgcolor")?.toLowerCase() === "#ffffff" ||
                el.getAttribute("bgcolor")?.toLowerCase() === "white"
            ) {
                el.removeAttribute("bgcolor");
            }
        });

        // NOTE: Init_O (called inside runInitAndMark, above) reshuffles the DOM.
        // Because option extraction already ran above and stores each option's
        // real KSD_TR_N id (not its position), the sidebar stays correct even
        // though the row order in the iframe changes after this point.

        if (qnType === 'B') {
            setTimeout(() => { extractOptionsTable(doc); adjustIframeHeight(iframe); }, 600);
        } else if (qnType === 'C') {
            setTimeout(() => { extractOptionsTable(doc); adjustIframeHeight(iframe); }, 700);
        } else if (qnType === 'M') {
            setTimeout(() => { extractOptionsTable(doc); adjustIframeHeight(iframe); }, 800);
        }

        // W type: capture correct answer and wrong answer for display below iframe
        if (qnType === 'W') {
            setTimeout(() => {
                const answer = (doc.getElementById('KSD_MW_CA') as HTMLInputElement)?.value || '';
                setWTypeAnswer(answer);
                setWTypeWrongAnswer(entWrongAns || '');
            }, 600);
        }

        iframe.style.overflow = 'visible';
        const doAdjust = () => adjustIframeHeight(iframe);
        setTimeout(doAdjust, 100);
        setTimeout(doAdjust, 400);
        setTimeout(doAdjust, 1000);

        const imgs = doc.querySelectorAll('img');
        if (imgs.length > 0) {
            let loaded = 0;
            const total = imgs.length;
            const onImgSettle = () => {
                loaded++;
                if (loaded === total) setTimeout(doAdjust, 100);
            };
            imgs.forEach((img: any) => {
                if (img.complete) { onImgSettle(); return; }
                img.addEventListener('load', onImgSettle);
                img.addEventListener('error', onImgSettle);
            });
        }
    }, [data, runInitAndMark, injectPlaySoundBridge, entWrongAns, extractOptionsTable, optOrder]);

    // ---------------------------------------------------------------------------
    // Data fetching
    // ---------------------------------------------------------------------------
    useEffect(() => {
        if (!lessonId) { setLoading(false); return; }
        setLoading(true);
        setFTypeMeta(null);
        setWTypeAnswer('');
        setWTypeWrongAnswer('');
        setOptions([]);
        setLTypeOptions([]);
        setCorrectOptionIndexFallback(null);
        setExtractedOptionsHtml('');
        // Reset the "already processed" guard whenever a new question is requested
        processedDocRef.current = null;
        const url = `${API_BASE_URL}api/question_viewer_get_data?lesson_id=${lessonId}&country=${country}&qn_no=${qnNo}&mdcode=${mdcode}&eccode=${eccode}&from=${fromPage}`;
        fetch(url)
            .then(r => r.json())
            .then(json => { if (!json.error) setData(json); else console.error(json.error); })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [lessonId, country, qnNo, mdcode, eccode, fromPage, API_BASE_URL]);

    useEffect(() => {
        if (isFromInsights) {
            setInsightUrls(JSON.parse(sessionStorage.getItem('questionUrls') || '[]'));
            setInsightIndex(parseInt(sessionStorage.getItem('currentIndex') || '0', 10));
        }
    }, [isFromInsights]);

    useEffect(() => {
        if (testCode && effectiveDicCode && firstName) setQnPanelBg('#20699B');
    }, [testCode, effectiveDicCode, firstName]);

    useEffect(() => {
        const prevent = (e: MouseEvent) => e.preventDefault();
        document.addEventListener('contextmenu', prevent);
        const preventKeys = (e: KeyboardEvent) => {
            if ((e.altKey || e.ctrlKey) && e.keyCode === 37) { e.preventDefault(); return false as any; }
        };
        document.addEventListener('keydown', preventKeys);
        return () => {
            document.removeEventListener('contextmenu', prevent);
            document.removeEventListener('keydown', preventKeys);
        };
    }, []);

    useEffect(() => {
        if (!loading && searchWords) setTimeout(highlightInAllIframes, 600);
    }, [loading, searchWords, highlightInAllIframes]);

    useEffect(() => {
        if (!data) return;
        const iframe = iframeQnRef.current;
        if (!iframe) return;
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc && doc.readyState === 'complete' && doc.body && doc.body.innerHTML.trim() !== '') {
            // Safe to call unconditionally — onQuestionLoad guards against
            // double-processing the same document via processedDocRef.
            onQuestionLoad();
        }
    }, [data, onQuestionLoad]);

    // ---------------------------------------------------------------------------
    // Navigation
    // ---------------------------------------------------------------------------

    const handleBack = () => {
        if (isFromInsights) {
            const fromDate = searchParams.get('Fromlsndate') || '';
            const toDate = searchParams.get('Tolsndate') || '';

            const params = new URLSearchParams();
            params.set('Title', lessonId);
            params.set('Qnno', qnNoStr);
            if (testCode) params.set('TestCode', testCode);
            if (effectiveDicCode) params.set('DICCode', effectiveDicCode);
            if (fromDate && toDate) {
                params.set('Fromlsndate', fromDate);
                params.set('Tolsndate', toDate);
            }

            window.location.href = `/SUPPORT/EL_QuestionInsights?${params.toString()}`;
        } else {
            let codeParam = '';
            if (mdcode) codeParam = `&mdcode=${mdcode}`;
            else if (eccode) codeParam = `&eccode=${eccode}`;
            window.location.href = `/LessonBrowser/LessonViewer?Lessonid=${lessonId}&Country=${country}${codeParam}`;
        }
    };


    const handlePrevious = () => {
        if (isFromInsights && insightIndex > 0) {
            const prev = insightIndex - 1;
            sessionStorage.setItem("currentIndex", prev.toString());
            window.location.href = insightUrls[prev];
            return;
        }

        if (!isFromInsights && currentIndex > 0) {
            const prevQn = lessonQuestions[currentIndex - 1].qn_no;

            const codeParam = mdcode
                ? `&mdcode=${mdcode}`
                : eccode
                    ? `&eccode=${eccode}`
                    : "";

            window.location.href =
                `/LessonBrowser/QuestionViewer?Lessonid=${lessonId}` +
                `&Country=${country}` +
                `&Qnno=${prevQn}${codeParam}`;
        }
    };

    const handleNext = () => {
        if (isFromInsights && insightIndex < insightUrls.length - 1) {
            const next = insightIndex + 1;
            sessionStorage.setItem("currentIndex", next.toString());
            window.location.href = insightUrls[next];
            return;
        }

        if (
            !isFromInsights &&
            currentIndex >= 0 &&
            currentIndex < lessonQuestions.length - 1
        ) {
            const nextQn = lessonQuestions[currentIndex + 1].qn_no;

            const codeParam = mdcode
                ? `&mdcode=${mdcode}`
                : eccode
                    ? `&eccode=${eccode}`
                    : "";

            window.location.href =
                `/LessonBrowser/QuestionViewer?Lessonid=${lessonId}` +
                `&Country=${country}` +
                `&Qnno=${nextQn}${codeParam}`;
        }
    };


    // ---------------------------------------------------------------------------
    // Display helpers
    // ---------------------------------------------------------------------------
    const totalQns = data?.total_qns || 0;
    const showPrev = !isFromSearchQn && (isFromInsights ? insightIndex > 0 : qnNo > 1);
    const showNext = !isFromSearchQn && (isFromInsights ? insightIndex < insightUrls.length - 1 : qnNo < totalQns);
    const showBack = !isFromSearchQn && !isFromStudentPerf;
    const showClose = isFromStudentPerf;
    const countText = isFromInsights && insightUrls.length > 0
        ? `Question : ${insightIndex + 1} of ${insightUrls.length}`
        : `Question : ${currentIndex >= 0 ? currentIndex + 1 : qnNo
        } of ${totalQns}`;


    // ---------------------------------------------------------------------------
    // Sidebar status helpers
    // ---------------------------------------------------------------------------
    const parseNumericIdSet = (raw: string): Set<number> => {
        const s = new Set<number>();
        if (!raw) return s;
        raw.split('~').forEach(t => {
            const n = parseInt(t.trim(), 10);
            if (!Number.isNaN(n)) s.add(n);
        });
        return s;
    };

    const qnType = data?.qn_details?.qn_type?.toUpperCase() || '';

    const { correctIndices, selectedCorrectIndices, selectedWrongIndices } = (() => {
        if (qnType === 'C') {
            const parts = entCorAns.split('#');
            const correctRaw = parts[0] || '';
            const selectedRaw = parts[1] || '';

            const correctSet = parseNumericIdSet(correctRaw);

            let selectedSet: Set<number>;
            if (selectedRaw) {
                const selectedParts = selectedRaw.split('~');
                if (selectedParts.length === 1 && selectedParts[0].length > 1 && !/~/.test(selectedRaw)) {
                    selectedSet = new Set(selectedParts[0].split('').map(Number).filter(n => !Number.isNaN(n)));
                } else {
                    selectedSet = parseNumericIdSet(selectedRaw);
                }
            } else {
                selectedSet = new Set(correctSet);
            }
            return {
                correctIndices: correctSet,
                selectedCorrectIndices: selectedSet,
                selectedWrongIndices: parseNumericIdSet(entWrongAns),
            };
        }

        if (qnType === 'D') {
            const parseDropIdx = (raw: string) => {
                const s = new Set<number>();
                if (!raw) return s;
                raw.split('~').forEach(t => {
                    const n = parseInt(t.substring(0, 2), 10);
                    if (!Number.isNaN(n)) s.add(n);
                });
                return s;
            };
            const selected = parseDropIdx(entCorAns);
            return { correctIndices: selected, selectedCorrectIndices: selected, selectedWrongIndices: parseDropIdx(entWrongAns) };
        }

        // O, B, M, L, I types
        const selected = parseNumericIdSet(entCorAns);
        return {
            correctIndices: selected.size > 0 ? selected : (correctOptionIndexFallback !== null ? new Set([correctOptionIndexFallback]) : new Set<number>()),
            selectedCorrectIndices: selected,
            selectedWrongIndices: parseNumericIdSet(entWrongAns),
        };
    })();

    const getOptionStatus = (idx: number): 'correct' | 'wrong' | 'none' => {
        if (qnType === 'B') return correctOptionIndexFallback === idx ? 'correct' : 'none';
        if (selectedWrongIndices.has(idx)) return 'wrong';
        if (selectedCorrectIndices.has(idx)) return 'correct';
        if (correctIndices.has(idx)) return 'correct';
        return 'none';
    };

    const formatLessonHeader = (lessonName?: string, lessonCode?: string) => {
        const trimmedName = lessonName?.trim();
        if (trimmedName) {
            if (lessonCode && !trimmedName.includes('(') && !trimmedName.includes(')')) {
                return `${trimmedName} (${lessonCode})`;
            }
            return trimmedName;
        }
        return lessonCode || '';
    };

    const isPopupWindow = !!window.opener;

    const qnPanelStyle: React.CSSProperties = { backgroundColor: qnPanelBg };
    const hasClue = !!(data?.content?.clue);
    const hasPrinciples = !!(data?.content?.principles?.some(p => p));
    const hasSolution = !!(data?.content?.solution);
    const noClue = data?.qn_details?.no_clue ?? 0;
    const noPrc = data?.qn_details?.no_prc ?? 0;
    const noSol = data?.qn_details?.no_sol ?? 0;

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    return (
        <div style={{ backgroundColor: '#ffffff', minHeight: '100vh', fontFamily: 'Arial,sans-serif' }}>
            {loading && <Loading />}

            {/* Nav row */}
            <table id="tabQnNav" style={{ width: '100%', padding: 0, margin: 0, borderCollapse: 'collapse' }}>
                <tbody>
                    <tr id="trQnOK" style={{ height: '50px' }}>
                        <td style={{ width: '13%', textAlign: 'left', ...qnPanelStyle }} className="QnPanel">
                            {!isPopupWindow && (
                                <GifBtn name="back" visible={showBack} onClick={handleBack} marginLeft="13px" />
                            )}
                            <GifBtn name="close" visible={showClose} onClick={() => window.close()} marginLeft="13px" />
                        </td>
                        <td style={{ width: '13%', textAlign: 'center', display: noPrc > 0 ? 'table-cell' : 'none', ...qnPanelStyle }} className="QnPanel" />
                        <td style={{ width: '13%', textAlign: 'center', padding: 0, margin: 0, display: noClue > 0 ? 'table-cell' : 'none', ...qnPanelStyle }} className="QnPanel" />
                        <td style={{ width: '13%', textAlign: 'center', padding: 0, margin: 0, display: noSol > 0 ? 'table-cell' : 'none', ...qnPanelStyle }} className="QnPanel" />
                        <td style={{ width: '2%', ...qnPanelStyle }} className="QnPanel">&nbsp;</td>
                        <td id="tdprevious" style={{ width: '31%', textAlign: 'right', display: showPrev ? 'table-cell' : 'none', ...qnPanelStyle }} className="QnPanel">
                            <GifBtn name="previous" visible={showPrev} onClick={handlePrevious} />
                        </td>
                        <td id="tdqncount" style={{ width: '31%', textAlign: 'right', ...qnPanelStyle }} className="QnPanel">
                            {!isFromSearchQn && (
                                <span id="spnqncount" style={{ color: 'white', textAlign: 'right', fontSize: '20px' }}>
                                    {countText}
                                </span>
                            )}
                        </td>
                        <td style={{ width: '2%', ...qnPanelStyle }} className="QnPanel">&nbsp;</td>
                        <td id="tdnext" style={{ width: '31%', textAlign: 'right', display: showNext ? 'table-cell' : 'none', ...qnPanelStyle }} className="QnPanel">
                            <GifBtn name="next" visible={showNext} onClick={handleNext} />
                        </td>
                        <td id="tdOK" style={{ width: '31%', textAlign: 'right', ...qnPanelStyle }} className="QnPanel">
                            <img id="imgOK" src="/Image/ok_n.gif"
                                style={{ cursor: 'pointer', verticalAlign: 'middle', display: 'none' }} alt="ok"
                                onMouseOver={(e) => (e.currentTarget.src = '/Image/ok_o.gif')}
                                onMouseOut={(e) => (e.currentTarget.src = '/Image/ok_n.gif')}
                                onMouseDown={(e) => (e.currentTarget.src = '/Image/ok_d.gif')}
                                onMouseUp={(e) => (e.currentTarget.src = '/Image/ok_o.gif')}
                            />
                        </td>
                        <td style={{ width: '2%', ...qnPanelStyle }} className="QnPanel">&nbsp;</td>
                    </tr>
                </tbody>
            </table>

            {/* Title row */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                    <tr id="trQntitle">
                        <td id="tdQnTitle" style={{ padding: '2px', width: '100%', backgroundColor: 'transparent', fontSize: '16pt' }} className="HeadMsg">
                            <div style={{ color: 'black', fontWeight: 'bold', fontSize: '16px', lineHeight: '2.2' }}>
                                {effectiveDicCode && <div id="spnDiccode">DICCode :{effectiveDicCode}</div>}
                                {(testCode && effectiveDicCode && firstName) && <div id="spnTestcode">Testcode :{testCode}</div>}
                                {familyCode && <div id="spnFamily">Family :{familyCode}</div>}
                                {firstName && <div id="spnLogin">Firstname :{firstName}</div>}
                            </div>
                            <br />
                            <span id="spanLessonName" style={{ color: '#800000', marginLeft: '10px', fontWeight: 'bold', fontSize: '18px' }}>
                                {formatLessonHeader(data?.lesson_name, lessonId)}
                            </span>
                            <span style={{ color: '#800000', fontWeight: 'bold', fontSize: '18px' }}>
                                &nbsp;&nbsp;-&nbsp;&nbsp;Question&nbsp;&nbsp;
                            </span>
                            &nbsp;
                            <span id="spanCurrentQuestionNo" style={{ color: 'maroon', fontWeight: 'bold' }}>
                                {String(qnNo).padStart(3, '0')}
                            </span>
                            &nbsp;&nbsp;
                            <span id="spnSolSeen" style={{ color: '#2f24da', display: solSeen === 'Y' ? 'inline' : 'none' }}>
                                (Solution Seen)
                            </span>
                            &nbsp;
                            <span id="spnQnSkipped" style={{ color: '#2f24da', display: entCorAns === 'QnSkipped' ? 'inline' : 'none' }}>
                                (Question Skipped)
                            </span>
                        </td>
                    </tr>
                </tbody>
            </table>

            {/* Main content */}
            <table id="tblmain" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                    <tr id="triFrame">

                        {/* Left 80%: iframes */}
                        <td style={{ width: '80%', verticalAlign: 'top' }}>

                            {data?.content?.question && (
                                <div style={{ marginLeft: '10px', marginTop: '4px', marginBottom: '2px' }}>
                                    <strong style={{ fontFamily: 'arial,sans-serif' }}>Question Contents:</strong>
                                </div>
                            )}

                            {/* Question iframe */}
                            <div id="divFrameQn" style={{ WebkitOverflowScrolling: 'touch', overflow: 'visible', marginLeft: '10px', marginRight: '10px' }}>
                                {data?.content?.question ? (
                                    <iframe
                                        ref={iframeQnRef}
                                        id="iFrameQn"
                                        srcDoc={data.content.question}
                                        style={{
                                            width: '100%', minHeight: '70px', border: '1px solid black',
                                            backgroundColor: 'white', display: 'block', boxSizing: 'border-box', overflow: 'hidden',
                                        }}
                                        className="CssiFrm"
                                        frameBorder={0}
                                        scrolling="no"
                                        title="Question"
                                        onLoad={onQuestionLoad}
                                    />
                                ) : null}
                            </div>

                            {/* L type: reordered options table (order reflects OptOrder,
                                i.e. what the student actually answered; label stays tied
                                to the original row so the correct letter is preserved). */}
                            {qnType === "L" && lTypeOptions.length > 0 && (
                                <>
                                    <div
                                        style={{
                                            marginLeft: "10px",
                                            marginTop: "8px",
                                            marginBottom: "2px"
                                        }}
                                    >
                                        <strong style={{ fontFamily: "arial,sans-serif" }}>
                                            Options:
                                        </strong>
                                    </div>

                                    <table
                                        style={{
                                            width: "100%",
                                            borderCollapse: "collapse",
                                            marginTop: "5px",
                                            marginLeft: "10px"
                                        }}
                                    >
                                        <tbody>
                                            {lTypeOptions.map((option, index) => (
                                                <tr key={index}>
                                                    <td
                                                        style={{
                                                            border: "1px solid #999",
                                                            padding: "10px",
                                                            width: "50px",
                                                            backgroundColor: "#ffffff",
                                                            color: "red",
                                                            fontWeight: "bold",
                                                            textAlign: "center",
                                                            verticalAlign: "middle"
                                                        }}
                                                    >
                                                        {option.label}
                                                    </td>

                                                    <td
                                                        style={{
                                                            border: "1px solid #999",
                                                            padding: "10px",
                                                            backgroundColor: "#ffffff",
                                                            color: "#000000",
                                                            verticalAlign: "middle"
                                                        }}
                                                    >
                                                        {option.text.includes("<") ? (
                                                            <div
                                                                dangerouslySetInnerHTML={{
                                                                    __html: option.text
                                                                }}
                                                            />
                                                        ) : (
                                                            option.text
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            {/* W type: correct answer + wrong answer below iframe */}
                            {qnType === 'W' && wTypeAnswer && (
                                <div style={{ marginLeft: '20px', marginTop: '8px', fontSize: '18px', fontFamily: 'Arial' }}>
                                    <strong>Answer:</strong> <span style={{ color: 'green' }}>{wTypeAnswer}</span>
                                </div>
                            )}
                            {qnType === 'W' && wTypeWrongAnswer && (
                                <div style={{ marginLeft: '20px', marginTop: '4px', fontSize: '16px', fontFamily: 'Arial', color: '#c00', fontWeight: 'bold' }}>
                                    Entered wrong answer : {wTypeWrongAnswer}
                                </div>
                            )}

                            {/* F type: Dummy words */}
                            {qnType === 'F' && fTypeMeta && fTypeMeta.dummyWords.length > 0 && (
                                <div style={{ marginLeft: '10px', marginTop: '10px' }}>
                                    <strong style={{ fontFamily: 'arial,sans-serif' }}>Dummy words:</strong>
                                    <ul style={{ marginTop: '4px', paddingLeft: '24px', fontFamily: 'Arial', fontSize: '16px' }}>
                                        {fTypeMeta.dummyWords.map((w, i) => (
                                            <li key={i}>{w}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* B / C / M: options table extracted directly from the (already marked)
                                question content — rendered once, under "Options:" */}
                            {['B', 'C', 'M'].includes(qnType) && extractedOptionsHtml && (
                                <>
                                    <div style={{ marginLeft: '10px', marginTop: '8px', marginBottom: '2px' }}>
                                        <strong style={{ fontFamily: 'arial,sans-serif' }}>Options:</strong>
                                    </div>
                                    <div
                                        style={{ marginLeft: '10px', marginRight: '10px', marginTop: '5px' }}
                                        dangerouslySetInnerHTML={{ __html: extractedOptionsHtml }}
                                    />
                                </>
                            )}

                            {/* Options sidebar table — for O, I, D.
                                Keyed and status-checked by option.rowId (the real
                                KSD_TR_N id), not by positional index. */}
                            {data?.qn_details &&
                                ['O', 'I', 'D'].includes(data.qn_details.qn_type.toUpperCase()) &&
                                options.length > 0 && (
                                    <>
                                        <div style={{ marginLeft: '10px', marginTop: '8px', marginBottom: '2px' }}>
                                            <strong style={{ fontFamily: 'arial,sans-serif' }}>Options:</strong>
                                        </div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '5px', marginLeft: '10px' }}>
                                            <tbody>
                                                {options.map((option) => {
                                                    const status = getOptionStatus(option.rowId);
                                                    return (
                                                        <tr key={option.rowId}>
                                                            <td style={{ border: '1px solid #999', padding: '10px', backgroundColor: '#ffffff', color: '#000000', fontWeight: 'bold' }}>
                                                                {option.text.includes('<') ? <div dangerouslySetInnerHTML={{ __html: option.text }} /> : option.text}
                                                            </td>
                                                            <td style={{ border: '1px solid #999', textAlign: 'center', width: '50px' }}>
                                                                {status === 'correct' && <span style={{ color: 'green', fontSize: '32px' }}>✓</span>}
                                                                {status === 'wrong' && <span style={{ color: 'red', fontSize: '32px' }}>✕</span>}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </>
                                )}

                            {/* Clue iframe */}
                            <div id="divFrameClu" style={{ overflow: 'hidden', display: (noClue > 0 && hasClue) ? 'block' : 'none' }}>
                                <h4 style={{ marginLeft: '10px', fontFamily: 'arial,sans-serif', marginBottom: '5px' }}>Clues</h4>
                                <iframe
                                    ref={iframeCluRef}
                                    id="iFrameClue"
                                    srcDoc={data?.content?.clue || '<html><body></body></html>'}
                                    style={{ width: '98.7%', border: '1.4px solid black', display: 'block', marginLeft: '10px', minHeight: '50px' }}
                                    frameBorder={0} scrolling="no" onLoad={onClueLoad} title="Clue"
                                />
                            </div>

                            {/* Principle iframes */}
                            <div id="divFramePrinciple" style={{ overflow: 'hidden', display: (noPrc > 0 && hasPrinciples) ? 'block' : 'none' }}>
                                <h4 style={{ marginLeft: '10px', fontFamily: 'arial,sans-serif', marginBottom: '0px' }}>Principle</h4>
                                {(data?.content?.principles || []).map((prcHtml, i) => (
                                    <iframe
                                        key={i}
                                        id={`iFramePrinciple${i + 1}`}
                                        ref={el => { prcIframeRefs.current[i] = el; }}
                                        srcDoc={prcHtml || '<html><body></body></html>'}
                                        style={{ width: '100%', border: 'none', display: 'block', minHeight: '50px' }}
                                        frameBorder={0} scrolling="no"
                                        onLoad={(e) => onPrincipleLoad(e.currentTarget)}
                                        title={`Principle ${i + 1}`}
                                    />
                                ))}
                            </div>

                            {/* Solution iframe */}
                            <div id="divFrameSol" style={{ overflow: 'hidden', display: (noSol > 0 && hasSolution) ? 'block' : 'none' }}>
                                <h4 style={{ marginLeft: '10px', fontFamily: 'arial,sans-serif', marginBottom: '5px' }}>Solutions</h4>
                                <iframe
                                    ref={iframeSolRef}
                                    id="iFrameSolution"
                                    srcDoc={data?.content?.solution || '<html><body></body></html>'}
                                    style={{ width: '100%', border: 'none', display: 'block', minHeight: '50px' }}
                                    frameBorder={0} scrolling="no" onLoad={onSolutionLoad} title="Solution"
                                />
                            </div>
                        </td>

                        {/* Right sidebar */}
                        <td id="tblrhs" style={{ verticalAlign: 'top', fontSize: '18px', width: '20%', padding: '15px', fontFamily: 'Times New Roman' }}>
                            <div style={{ paddingBottom: '4px' }}><strong>Type :</strong><span id="Questiontype">&nbsp;{data?.qn_details?.type_name}</span></div>
                            <div style={{ paddingBottom: '4px' }}><strong>Complexity :</strong><span id="Qncomplexity">&nbsp;{data?.qn_details?.complexity}</span></div>
                            <div style={{ paddingBottom: '4px' }}><strong>Used In Exercise :</strong><span id="Usedinexe">&nbsp;{data?.qn_details?.used_in_e}</span></div>
                            <div style={{ paddingBottom: '4px' }}><strong>Used In Test :</strong><span id="Usedintest">&nbsp;{data?.qn_details?.used_in_t}</span></div>

                            {/* F type extra metadata */}
                            {qnType === 'F' && fTypeMeta && (
                                <>
                                    {fTypeMeta.correctComment && (
                                        <div style={{ paddingBottom: '4px' }}>
                                            <strong>Correct Comment :</strong>
                                            <span>&nbsp;{fTypeMeta.correctComment}</span>
                                        </div>
                                    )}
                                    {fTypeMeta.wrongComment && (
                                        <div style={{ paddingBottom: '4px' }}>
                                            <strong>Wrong Comment :</strong>
                                            <span>&nbsp;{fTypeMeta.wrongComment}</span>
                                        </div>
                                    )}
                                    <div style={{ paddingBottom: '4px' }}>
                                        <strong>Drag and drop :</strong>
                                        <span>&nbsp;{fTypeMeta.dragDrop ? 'ON' : 'OFF'}</span>
                                    </div>
                                </>
                            )}
                        </td>

                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default QuestionViewer;